import type { DayMasterStrength, StrengthLevel } from '../types/strength';
import type { GyeokGukAnalysis } from '../types/gyeok-guk';

/**
 * 격국 우선 모드(geuk-first): 격국 판정 결과로 신강/신약 레벨을 보정한다.
 *
 * 원리: 자평 고전에서는 월령 제강 + 격국 성립 여부가 신강/신약의 상위 개념.
 * 기존 점수(score)는 세분화용으로 유지하되, 격국 유형에 따라 레벨을 조정한다.
 *
 * 보정 규칙:
 * - 종강격: 극왕 강제 (이미 극강이므로 기세를 따름)
 * - 종약격(종아/종재/종살): 극약 강제 (극약이므로 기세를 따름)
 * - 국격/건록격/양인격: 최소 신강 보장 (기세 결집 또는 월지 뿌리 강함)
 * - 정격 성격: 한 단계씩 완화 (극왕→태강, 태강→신강, 극약→태약, 태약→신약)
 * - 파격: 보정 없음 (격이 깨졌으므로 점수 기반 판단 유지)
 */
export function adjustStrengthByGyeokGuk(
  strength: DayMasterStrength,
  gyeokGuk: GyeokGukAnalysis,
): DayMasterStrength {
  const adjusted = getAdjustedLevel(strength.level, gyeokGuk);

  return {
    ...strength,
    level: adjusted,                    // 후속 분석(용신 등)에서 보정된 레벨 사용
    adjustedLevel: adjusted,            // 보정 결과 명시
    originalLevel: strength.level,      // 보정 전 원래 레벨 보존
  };
}

function getAdjustedLevel(
  level: StrengthLevel,
  gyeokGuk: GyeokGukAnalysis,
): StrengthLevel {
  const { type, category, status } = gyeokGuk;

  // 파격이면 보정 없음 — 점수 기반 판단 유지
  if (status === '파격') return level;

  // 종격: 극단적 강약 강제
  if (category === '종격') {
    if (type === '종강격') return '극왕';
    // 종아격, 종재격, 종살격
    return '극약';
  }

  // 국격, 건록격, 양인격: 최소 신강 보장
  if (category === '국' || type === '건록격' || type === '양인격') {
    return isStrongSide(level) ? level : '신강';
  }

  // 정격 성격: 극단적 강약을 완화
  if (category === '정격') {
    return moderateLevel(level);
  }

  return level;
}

/** 신강 계열인지 확인 */
function isStrongSide(level: StrengthLevel): boolean {
  return ['중화신강', '신강', '태강', '극왕'].includes(level);
}

/**
 * 정격 성격일 때 극단적 강약을 완화
 * - 극왕 → 태강, 태강 → 신강 (한 단계씩 완화)
 * - 극약 → 태약, 태약 → 신약 (한 단계씩 완화)
 * - 나머지 → 유지
 */
function moderateLevel(level: StrengthLevel): StrengthLevel {
  switch (level) {
    case '극왕': return '태강';
    case '태강': return '신강';
    case '극약': return '태약';
    case '태약': return '신약';
    default: return level;
  }
}
